/* eslint-disable @williamhill/shared-is-not-really-a-package-import */
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useQuery } from 'react-apollo';

import { showInfoModal } from '@williamhill/rosi-ui-shared/src/components/modal';
import { getDeviceInfo } from '@williamhill/rosi-ui-shared/src/lib/device-info';
import { IBet, ResultType } from '@williamhill/rosi-ui-shared/src/types';
import { BetInfo, Props as BetInfoProps } from './bet-info';
import BetLegs, { Leg as BetLeg } from './bet-legs';
import ParimutuelBetLegs, { Leg as ParimutuelBetLeg } from './parimutuel-bet-legs';
import BetTotals, { CashOutLabelDetails } from './bet-totals';
import * as Lib from './lib';
import { VENUE_NAME_QUERY } from '@williamhill/rosi-ui-shared/src/lib/queries';
import SuperParlayBetLegs, { SP_GRID_TEMPLATE_COLUMNS } from './superparlay-bet-legs';
import { IBetLeg, ILeg, PlayerCardOperator } from '../../types';
import Header, { Props as HeaderProps } from './header';
import { formatMoneyWithSymbol } from '../../lib/money-formatter';
import { Block } from '../typography';
import { IIcon, InjuryProtection as InjuryProtectionIcon } from '../../assets/icons';

type Props = {
  bet: { legs: ILeg[]; betLegs: IBetLeg[] } & Pick<
    IBet,
    | 'settled'
    | 'totalStake'
    | 'type'
    | 'rewardCreditsPerLine'
    | 'potentialReturns'
    | 'originalPotentialReturns'
    | 'estimatedOdds'
    | 'price'
    | 'originalPrice'
    | 'toWin'
    | 'originalToWin'
    | 'showVoidedAdjustmentMessage'
  > &
    BetInfoProps['bet'] &
    Parameters<typeof useDifferentVenueWarning>[0] &
    Parameters<typeof Lib.getAdjustedReturns>[0] &
    Parameters<typeof Lib.getFormattedStake>[0] &
    Parameters<typeof Lib.getFormattedWinnings>[0];
  getLocalDateTime(dateTime: Date): Date;
  isCashedOut: boolean;
  canCashOut: boolean;
  cashOutLabelDetails?: CashOutLabelDetails;
  playerCardOperator?: PlayerCardOperator;
} & HeaderProps;

export type BetDetailsVariant = 'default' | 'compact';

export type ViewProps = Props & {
  legsStyle?: {
    gridTemplateColumns: string;
    isCompetitionSportVisible: boolean;
    isEventStartTimeVisible: boolean;
    marginHorizontalInRem: number;
    paddingHorizontal: string;
  };
  venue: BetInfoProps['venue'];
  variant?: BetDetailsVariant;
};

export type Leg = BetLeg & ParimutuelBetLeg;

export type ResultIconProps = { color: string; content: string };
export type SportsResultIconProps = ResultIconProps | React.FC<IIcon>;

export const iconsByResultType: Record<Exclude<ResultType, 'injury_protection'>, ResultIconProps> = {
  won: { color: 'success', content: 'W' },
  lost: { color: 'error', content: 'L' },
  void: { color: 'expired', content: 'V' },
  push: { color: 'warning', content: 'P' },
  place: { color: 'error', content: 'P' }
};

export const sportsIconsByResultType: Record<ResultType, SportsResultIconProps> = {
  ...iconsByResultType,
  injury_protection: InjuryProtectionIcon
};

export const inferSGPResult = (
  legs: { id: string; result?: { type: ResultType } | null }[],
  betLeg: { type: string; legRefs: string[] }
) => {
  const sgpLegs = legs
    .filter(leg => betLeg.type === 'leg-group' && betLeg.legRefs.includes(leg.id))
    .map(leg => leg.result?.type ?? null);

  const voidResultTypes: ResultType[] = ['void', 'injury_protection'];

  if (sgpLegs.includes(null)) {
    return '';
  } else if (sgpLegs.every(resultType => resultType && voidResultTypes.includes(resultType))) {
    return 'void';
  } else if (sgpLegs.includes('lost')) {
    return 'lost';
  }
  return 'won';
};

export default function BetDetails(props: Props & { fullWidth?: boolean }) {
  const venue = useDifferentVenueWarning(props.bet);

  return (
    <BetDetailsBox fullWidth={props.fullWidth}>
      <BetDetailsView {...props} {...{ venue }} />
    </BetDetailsBox>
  );
}

export function useDifferentVenueWarning({ placedAtLocation }: Pick<IBet, 'placedAtLocation'>) {
  const { slotId } = getDeviceInfo() || {};
  const betVenueId = placedAtLocation?.venueId;
  const isSameVenue = slotId?.venueId === betVenueId;
  const showSameVenueWarning = !isSameVenue && ['POS', 'CAGE'].includes(slotId?.type);

  useEffect(() => {
    showSameVenueWarning &&
      showInfoModal({
        title: `This ticket was issued at ${betVenueId}`,
        context: <>This may inhibit it from being cashed.</>
      });
  }, [showSameVenueWarning]);

  const { data } = useQuery(VENUE_NAME_QUERY, {
    skip: !placedAtLocation,
    variables: {
      venueId: placedAtLocation?.venueId
    }
  });

  return {
    label: data && data.venueByVenueId ? data.venueByVenueId.name : placedAtLocation?.venueId,
    isHighlighted: showSameVenueWarning
  };
}

export function BetDetailsView({
  label,
  status,
  canCashOut,
  bet,
  hasReprint = false,
  getLocalDateTime,
  legsStyle,
  venue,
  isCashedOut,
  cashOutLabelDetails,
  playerCardOperator,
  variant = 'default'
}: ViewProps) {
  const isParimutuel = bet.type === 'PARI_MUTUEL';
  const isSuperParlay = bet.betLegs != null && bet.betLegs.length > 0;
  const adjustedReturns = Lib.getAdjustedReturns(bet);
  const settledForLess = bet.totalStake.getValue() >= adjustedReturns.getValue();

  const showInjuryProtectionMessage = bet.legs.some(leg => leg.result?.type === 'injury_protection');

  return (
    <Wrapper size="sm" weight="bold" className="qa-bet-finder-details">
      <Header {...{ label, status, canCashOut, hasReprint, variant }} />
      <BetInfo {...{ bet, getLocalDateTime, venue, variant }} />
      <BetLegsContainer marginHorizontalInRem={legsStyle?.marginHorizontalInRem || 2}>
        {isParimutuel ? (
          <ParimutuelBetLegs legs={bet.legs} getLocalDateTime={getLocalDateTime} />
        ) : isSuperParlay ? (
          <SuperParlayBetLegs
            {...legsStyle}
            {...{ getLocalDateTime }}
            gridTemplateColumns={legsStyle?.gridTemplateColumns || SP_GRID_TEMPLATE_COLUMNS}
            legs={bet.legs}
            type={bet.type}
            spBetLegs={bet.betLegs}
            variant={variant}
          />
        ) : (
          <BetLegs {...legsStyle} {...{ getLocalDateTime }} legs={bet.legs} type={bet.type} variant={variant} />
        )}
      </BetLegsContainer>
      <BetTotals
        betStatus={bet.betStatus}
        stake={Lib.getFormattedStake(bet)}
        potentialReturns={formatMoneyWithSymbol(adjustedReturns, '$')}
        originalToWin={bet.originalToWin ? formatMoneyWithSymbol(bet.originalToWin, '$') : undefined}
        winAmount={formatMoneyWithSymbol(bet.toWin, '$')}
        showInjuryProtectionMessage={showInjuryProtectionMessage}
        showVoidedAdjustmentMessage={bet.showVoidedAdjustmentMessage}
        settled={bet.settled}
        withheldAmount={Lib.getFormattedWithholding(bet)}
        isParimutuel={bet.type === 'PARI_MUTUEL'}
        variant={variant}
        rewardCreditsPerLine={bet.rewardCreditsPerLine}
        {...{ isCashedOut, cashOutLabelDetails, settledForLess, playerCardOperator }}
      />
    </Wrapper>
  );
}

export const BetDetailsBox = styled.div<{ fullWidth?: boolean }>`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px 4px 0 0;
  flex: 1;
  overflow: hidden;
  margin: 0 2.4rem;
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      margin: 0 !important;
      width: 100%;
    `}
`;

const Wrapper = styled(Block)`
  display: flex;
  flex-direction: column;
  height: 100%;
  text-align: center;
`;

const BetLegsContainer = styled.div<{ marginHorizontalInRem: number }>`
  flex: 1 1 0;
  overflow: auto;
`;
